import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BuildLogsList } from "@/components/Deploy/BuildLogsList";
import { DEPLOY_STATUS } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import { Deploy } from "@/types";
import { BarChart3, Globe, RefreshCw, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface OverviewTabProps {
  deploy: Deploy;
  onRedeploy: () => void;
  onViewLogs: () => void;
  onDelete: () => void;
  onManageDomains: () => void;
  onCopyToClipboard: (text: string) => void;
  onOpenInNewPage: (url: string) => void;
}

// RESTORED: Exact original styling constants
const panelClass =
  "rounded-2xl border border-slate-800/80 bg-slate-950/60 shadow-sm";
const panelHeaderClass =
  "flex flex-col gap-1 border-b border-slate-800/70 px-5 py-4";
const panelBodyClass = "px-5 py-4";
const detailCardClass =
  "rounded-xl border border-slate-800/70 bg-slate-950/40 px-4 py-3";

// Helper for clean data rows within the panels
const DataRow = ({
  label,
  value,
  action,
}: {
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-800/50 last:border-0 last:pb-0 first:pt-0">
    <span className="text-sm text-slate-500 font-medium">{label}</span>
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-200 text-right">{value}</span>
      {action}
    </div>
  </div>
);

export function OverviewTab({
  deploy,
  onRedeploy,
  onDelete,
  onManageDomains,
  onCopyToClipboard,
  onOpenInNewPage,
}: OverviewTabProps) {
  const navigate = useNavigate();
  const isDeploying = deploy.status === DEPLOY_STATUS.IN_PROGRESS;
  const isDeleted = deploy.status === DEPLOY_STATUS.DELETED;
  const domainToCopy = deploy.customDomain || deploy.subDomain || "";
  const primaryDomain = domainToCopy || "Pending";
  const summaryStats = useMemo(
    () => [
      {
        label: "Environment",
        value: humanizeString(deploy.env) || "Unknown",
      },
      {
        label: "Region",
        value: deploy.region || "Unknown",
      },
      {
        label: "Branch",
        value: deploy.branch || "Unknown",
      },
      {
        label: "Commit",
        value: deploy.latestCommitHash
          ? deploy.latestCommitHash.substring(0, 7)
          : "Pending",
      },
    ],
    [deploy.branch, deploy.env, deploy.latestCommitHash, deploy.region],
  );

  const actionButtons = [
    {
      key: "domain",
      label: "Custom domain",
      helper: "Attach or update a domain",
      onClick: onManageDomains,
      disabled: isDeploying || isDeleted,
      icon: Globe,
    },
    {
      key: "redeploy",
      label: isDeploying ? "Deploying..." : "Redeploy",
      helper: "Trigger a fresh build",
      onClick: onRedeploy,
      disabled: isDeploying || isDeleted,
      primary: true,
      icon: RefreshCw,
    },
    {
      key: "metrics",
      label: "View Metrics",
      helper: "Runtime charts & usage",
      onClick: () => navigate(`/metrics?deployId=${deploy.id}`),
      disabled: isDeleted,
      icon: BarChart3,
    },
    {
      key: "delete",
      label: "Delete Service",
      helper: "Permanently remove deployment",
      onClick: onDelete,
      disabled:
        deploy.status === DEPLOY_STATUS.DELETING ||
        deploy.status === DEPLOY_STATUS.IN_PROGRESS ||
        deploy.status === DEPLOY_STATUS.DELETED,
      destructive: true,
      icon: Trash2,
    },
  ];

  const formatDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime || startTime === "0001-01-01T00:00:00Z")
      return "N/A";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = Math.round((end.getTime() - start.getTime()) / 1000);
    return `${Math.floor(duration / 60)}m ${duration % 60}s`;
  };

  return (
    <div className="space-y-6">
      {/* 1. Summary Stats - Using detailCardClass for the grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryStats.map((stat) => (
          <div
            key={stat.label}
            className={`${detailCardClass} flex flex-col justify-center gap-1`}
          >
            <span className="text-[11px] font-medium text-slate-500 uppercase tracking-widest">
              {stat.label}
            </span>
            <span className="text-sm font-semibold text-slate-100 truncate">
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {/* 2. Build Logs (Conditional) */}
      {isDeploying && (
        <Card className={panelClass}>
          <CardHeader className={panelHeaderClass}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-semibold text-slate-100">
                  Build in progress
                </CardTitle>
                <CardDescription className="text-xs text-slate-400">
                  Streaming logs from the build server
                </CardDescription>
              </div>
              <Badge className="border border-amber-500/40 bg-amber-500/10 text-amber-200">
                Building...
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-0">
            <div className="h-[360px] overflow-y-auto">
              <div className="space-y-3">
                <BuildLogsList deploy={deploy} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. Main Data Panels */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column: Application Context */}
        <div className="space-y-6">
          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className="text-sm font-semibold text-slate-100">
                Application Details
              </CardTitle>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              <DataRow
                label="Live URL"
                value={
                  domainToCopy ? (
                    <button
                      onClick={() => onOpenInNewPage(domainToCopy)}
                      disabled={isDeleted}
                      className="text-cyan-200 hover:text-cyan-100 underline decoration-cyan-500/30 underline-offset-4 transition-colors"
                    >
                      {primaryDomain}
                    </button>
                  ) : (
                    <span className="text-slate-400">{primaryDomain}</span>
                  )
                }
                action={
                  domainToCopy && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCopyToClipboard(domainToCopy)}
                      disabled={isDeleted}
                      className="h-6 px-2 text-xs text-slate-400 hover:text-slate-100"
                    >
                      Copy
                    </Button>
                  )
                }
              />
              <DataRow
                label="Environment"
                value={<span className="capitalize">{deploy.env}</span>}
              />
              <DataRow label="Region" value={deploy.region} />
              <DataRow
                label="Deploy Type"
                value={<span className="capitalize">{deploy.deployType}</span>}
              />
              <DataRow label="Public Port" value={deploy.port} />
            </CardContent>
          </Card>

          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className="text-sm font-semibold text-slate-100">
                System Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              <DataRow
                label="Allocated Memory"
                value={<span className="font-mono">{deploy.memoryMB} MB</span>}
              />
              <DataRow
                label="vCPU Cores"
                value={<span className="font-mono">{deploy.vCpu}</span>}
              />
              <DataRow
                label="Created At"
                value={new Date(deploy.createdAt).toLocaleString()}
              />
              <DataRow
                label="Build Duration"
                value={formatDuration(
                  deploy.buildStartTime,
                  deploy.buildEndTime,
                )}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Source & Actions */}
        <div className="space-y-6">
          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className="text-sm font-semibold text-slate-100">
                Source Control
              </CardTitle>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              <DataRow
                label="Repository"
                value={
                  <button
                    onClick={() => onOpenInNewPage(deploy.repoUrl)}
                    disabled={isDeleted}
                    className="text-cyan-200 hover:text-cyan-100 underline decoration-cyan-500/30 underline-offset-4 transition-colors break-all text-left"
                  >
                    {deploy.repoUrl.replace("https://github.com/", "")}
                  </button>
                }
              />
              <DataRow label="Branch" value={deploy.branch} />
              <DataRow
                label="Source Type"
                value={<span className="capitalize">{deploy.sourceType}</span>}
              />
              {deploy.rootDirectory && (
                <DataRow
                  label="Root Directory"
                  value={
                    <span className="font-mono">{deploy.rootDirectory}</span>
                  }
                />
              )}
            </CardContent>
          </Card>

          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <CardTitle className="text-sm font-semibold text-slate-100">
                Management
              </CardTitle>
              <CardDescription className="text-xs text-slate-400">
                Execute deployment operations
              </CardDescription>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {actionButtons.map((action) => (
                  <button
                    key={action.key}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={`
                      group relative flex w-full flex-col items-start justify-between gap-2 rounded-lg border px-4 py-3 text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed outline-none focus-visible:ring-1 focus-visible:ring-slate-700
                      ${
                        action.destructive
                          ? "border-rose-900/40 bg-rose-950/10 hover:bg-rose-950/20 hover:border-rose-700/60 text-rose-200 focus-visible:ring-rose-700"
                          : "border-slate-800/60 bg-slate-900/20 hover:bg-slate-800/40 hover:border-slate-700/80 text-slate-200"
                      }
                    `}
                  >
                    <div className="flex w-full items-start gap-3">
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors duration-200 ${
                          action.destructive
                            ? "border-rose-900/30 bg-rose-500/10 text-rose-400 group-hover:border-rose-700/50 group-hover:bg-rose-500/20 group-hover:text-rose-300"
                            : "border-slate-800 bg-slate-800/50 text-slate-400 group-hover:border-cyan-500/30 group-hover:bg-cyan-950/30 group-hover:text-cyan-400"
                        }`}
                      >
                        <action.icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0">
                        <span className="text-sm font-medium leading-none tracking-tight truncate w-full">
                          {action.label}
                        </span>
                        <span
                          className={`text-xs leading-normal line-clamp-2 ${
                            action.destructive
                              ? "text-rose-300/50 group-hover:text-rose-300/70"
                              : "text-slate-500 group-hover:text-slate-400"
                          }`}
                        >
                          {action.helper}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
